import { faClose, faShareNodes } from "@fortawesome/free-solid-svg-icons"
import { faFacebook, faInstagram, faTiktok, faWhatsapp } from "@fortawesome/free-brands-svg-icons"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import GetSpace from "../../../utils/space"
import InputField from "../../../components/InputField/InputField"
import { INPUT_SIZE } from "../../../configuration/InputSize"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { SIZE } from "../../../configuration/Sizes"
import { useCreateCompany } from "../../../hooks/Company/useCreateCompany"
import { useSaveCompany } from "../../../hooks/Company/useSaveCompany"
import { companyMemory } from "../../../Objects/Memory/Memory"
import { useEffect } from "react"
import { useGetCompany } from "../../../hooks/Company/useGetCompany"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { Permissions } from "../../../Enum/PermissionsType"
import { CanView } from "../../../components/CanView/CanView"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { useRegister } from "../../../hooks/Register/useRegister"
import { ViewRegister } from "../../../components/ViewRegister/ViewRegister"

interface SocialMediaPupopProps {
  onClose: () => void
}

export const SocialMediaPupop: React.FC<SocialMediaPupopProps> = ({ onClose }) => {
  const { t } = useTranslation("company")

  const { handleSaveCompany } = useSaveCompany()

  const { handleGetCompany } = useGetCompany()

  const canEdite = !usePermission(Permissions.CanEditeCompany)

  const { handleEnableRegister, handleDisableRegister, isRegisterEnable } = useRegister()

  const {
    instagramUrl,
    facebookUrl,
    whatsappUrl,
    tiktokUrl,
    handleInstagramUrlChange,
    handleFacebookUrlChange,
    handleWhatsappUrlChange,
    handleTiktokUrlChange,
    handleInitialValue,
    handleSubmitForm,
  } = useCreateCompany()

  useEffect(() => {
    handleInitialValue(companyMemory)
  }, [])

  const handleTrySave = () => {
    const data = handleSubmitForm()

    handleSaveCompany(data, () => {
      handleGetCompany(() => handleInitialValue(companyMemory))
      onClose()
    })
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("label_title_social_media")}
              iconColor={THEME.colors.iconBGColor}
              icon={faShareNodes}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey="pages.myrestaurant.mycompany.socialmediapupop.1"
                    variant="global"
                    title={t("helpPopup:global.socialMedia.title")}
                    description={t("helpPopup:global.socialMedia.description")}
                    bullets={t("helpPopup:global.socialMedia.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.socialMediaPupop.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <form
              className="p-2"
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()

                handleTrySave()
              }}
            >
              <InputField
                maxLength={INPUT_SIZE.COMPANY.socialUrl}
                type="link"
                placeholder={t("label_instagram_url")}
                icon={faInstagram}
                disable={canEdite}
                value={instagramUrl}
                onChange={handleInstagramUrlChange}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.socialMediaPupop.input.instagramUrl"
              />

              <GetSpace top={10} />

              <InputField
                maxLength={INPUT_SIZE.COMPANY.socialUrl}
                type="link"
                placeholder={t("label_facebook_url")}
                icon={faFacebook}
                disable={canEdite}
                value={facebookUrl}
                onChange={handleFacebookUrlChange}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.socialMediaPupop.input.facebookUrl"
              />

              <GetSpace top={10} />

              <InputField
                maxLength={INPUT_SIZE.COMPANY.socialUrl}
                type="link"
                placeholder={t("label_whatsapp_url")}
                icon={faWhatsapp}
                disable={canEdite}
                value={whatsappUrl}
                onChange={handleWhatsappUrlChange}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.socialMediaPupop.input.whatsappUrl"
              />

              <GetSpace top={10} />

              <InputField
                maxLength={INPUT_SIZE.COMPANY.socialUrl}
                type="link"
                placeholder={t("label_tiktok_url")}
                icon={faTiktok}
                disable={canEdite}
                value={tiktokUrl}
                onChange={handleTiktokUrlChange}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.socialMediaPupop.input.tiktokUrl"
              />

              <div className={defaultStyle.divisionBar}></div>

              <section className={defaultStyle.buttonsController}>
                <CanView permission={Permissions.CanEditeCompany}>
                  <Button
                    sizeNumber={SIZE.buttonCloseIconSize}
                    label={t("buttons:button_save")}
                    typeButton="submit"
                    type="primary"
                    size="medium"
                    bgColor={THEME.colors.buttonBGSaveColor}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.socialMediaPupop.button.save"
                  />
                </CanView>

                <GetSpace right={5} />

                <Button
                  label={t("buttons:button_register")}
                  typeButton="button"
                  type="primary"
                  size="medium"
                  bgColor={THEME.colors.buttonBGRegisterColor}
                  onClick={handleEnableRegister}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.socialMediaPupop.button.register"
                />
              </section>
            </form>

            {isRegisterEnable && (
              <ViewRegister id={companyMemory.id} typeEntity="CompanyEntityId" onClose={handleDisableRegister} />
            )}
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
